/**
 * ticketService.ts
 * 
 * Servicio unificado de tickets: generación de payload, resolución de config,
 * renderizado HTML para impresión y preview, generación de barcode.
 * 
 * NO modifica lógica de venta. Solo opera POST-VENTA.
 */

import { isNativePlatform } from '../utils/platform';
import { aplicarConfiguracionWeb, compactarMetadatosWeb, ConfiguracionTiqueteWeb, normalizarConfiguracionWeb } from './ticketWebConfig';
import { fetchAPI } from './apiClient';
import { formatTicketForPrint } from '../utils/ticketFormat';

// ─── Tipos ───────────────────────────────────────────────

export type TicketAncho = 80 | 58;
export type TicketFontWeight = 'normal' | 'bold' | 'extra-bold';
export type TicketFontSize = 'normal' | 'grande' | 'extra-grande' | 'gigante' | 'extra-gigante';

export interface TicketConfig {
  nombre: string;
  notas: string;
  ancho: TicketAncho;
  logoUrl: string | null;
  logoDataUrl: string | null;
  mostrarLogo: boolean;
  fontWeight: TicketFontWeight;
  fontSize: TicketFontSize;
  // ── FASE 1: RRR Configuración LOCAL (Logo + Ticket)
  nombreLocal?: string;  // nombre_ticket del local (fallback a localNombre del payload)
  leyendaLocal?: string; // leyenda_ticket del local (fallback a "GRACIAS POR SU PREFERENCIA")
  tipoImpresora?: string; // "80mm" | "58mm" (para ancho dinámico)
  configuracionWeb?: ConfiguracionTiqueteWeb | null;
  esPropio?: boolean; // true si el local es propio
}

export interface TicketConfigApiResponse {
  configuracion_tiquete_web?: ConfiguracionTiqueteWeb | null;
  banca_nombre?: string | null;
  banca_logo?: string | null;
  ticket_nombre_default: string | null;
  ticket_notas_default: string | null;
  ticket_mostrar_logo: boolean | null;
  imprimir_logo?: boolean | null;
  tipo_impresora?: string | null;
  nombre_ticket?: string | null;
  leyenda_ticket?: string | null;
  telefono?: string | null;
  es_propio?: boolean | null;
}

/** Ajustes visuales almacenados en localStorage */
export interface TicketVisualConfig {
  fontWeight?: TicketFontWeight;
  fontSize?: TicketFontSize;
}

// localStorage keys
const LS_VISUAL_LOCAL = 'tiempos_ticket_visual_local';

export function guardarVisualConfig(cfg: TicketVisualConfig): void {
  localStorage.setItem(LS_VISUAL_LOCAL, JSON.stringify(cfg));
  invalidarConfigCache();
}

export function leerVisualConfig(): TicketVisualConfig {
  try {
    const raw = localStorage.getItem(LS_VISUAL_LOCAL);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export interface TicketJugada {
  num1: string;
  num2?: string | null;
  monto: number;
  tipoSorteo?: string; // e.g. 'TICA', 'REVENTADO', 'MONAZOS', 'PARLEY', 'FECHAS', 'MEGA'
}

type BloqueJugadas =
  | { tipo: 'general'; monto: string; lineas: string[] }
  | { tipo: 'monazos'; titulo: string; monto: string; lineas: string[] }
  | { tipo: 'titulo'; texto: string }
  | { tipo: 'reventado-tabla'; filas: Array<{ numeros: string; normal: string; reventado: string }> };

interface PremioTicketLinea {
  etiqueta: string;
  valor: string;
}

export interface TicketLocalInfo {
  nombre: string;
  descripcion?: string | null;
  telefono?: string | null;
  logo?: string | null;
  esPropio?: boolean;
  grupoId?: string | null;
}

export interface TicketPayload {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha: string;
  fecha: string;
  vendedorNombre: string;
  localNombre: string;
  local: TicketLocalInfo;
  esPropio: boolean;
  metodoPago: string;
  referencia: string | null;
  total: number;
  jugadas: TicketJugada[];
  config: TicketConfig;
  esReimpresion: boolean;
  tipoTicket: string;
  pagaVecesAplicado: string | null;
  clienteNombre: string;
  estado?: string;
  fechaAnulacion?: string;
}

export interface ResumenPremiosLinea {
  ticket: string;
  monto: number;
  premio: number;
}

export interface ResumenPremiosPayload {
  local: TicketLocalInfo;
  sorteo: string;
  estado?: string | null;
  fecha: string;
  ganador: string;
  tickets: ResumenPremiosLinea[];
  totalPagado: number;
  totalRecaudado: number;
  sorteoCierre?: string | null;
}

// ─── Config cache ────────────────────────────────────────

let _configCache: TicketConfig | null = null;
let _configCacheTime = 0;
const CONFIG_TTL = 30_000; // 30s

/**
 * Resuelve configuración del LOCAL con fallback a defaults.
 */
export async function resolverTicketConfig(): Promise<TicketConfig> {
  const now = Date.now();
  if (isNativePlatform() && _configCache && now - _configCacheTime < CONFIG_TTL) {
    return _configCache;
  }

  const cfg: TicketConfig = {
    nombre: 'TiemPos',
    notas: '',
    ancho: 80,
    logoUrl: null,
    logoDataUrl: null,
    mostrarLogo: true,
    fontWeight: 'bold',
    fontSize: 'normal',
    // ── FASE 1: RRR Configuración LOCAL (Logo + Ticket)
    nombreLocal: undefined,
    leyendaLocal: undefined,
    tipoImpresora: '80mm',
  };

  // ── Visual config desde localStorage (local) ──
  const visualLocal = leerVisualConfig();
  cfg.fontWeight = visualLocal.fontWeight || 'bold';
  cfg.fontSize = visualLocal.fontSize || 'normal';

  // ── Config del local (API) ──
  try {
    const local = await fetchAPI<TicketConfigApiResponse>(isNativePlatform() ? '/operaciones/local/ticket-config' : '/operaciones/local/ticket-config-web')
      .catch(error => {
        if (isNativePlatform()) throw error;
        // Backend anterior o migración pendiente: preservar configuración y formato existentes.
        return fetchAPI<TicketConfigApiResponse>('/operaciones/local/ticket-config');
      });
    if (!isNativePlatform()) cfg.configuracionWeb = local.configuracion_tiquete_web;

    // Branding centralizado: solo BANCA define nombre/logo.
    if (local.banca_nombre && cfg.nombre === 'TiemPos') cfg.nombre = local.banca_nombre;
    if (local.banca_logo) cfg.logoUrl = local.banca_logo;
    cfg.mostrarLogo = Boolean(local.banca_logo);

    // Configuración operativa del local (permitida)
    if (local.ticket_notas_default && !cfg.notas) cfg.notas = local.ticket_notas_default;
    if (local.leyenda_ticket) cfg.leyendaLocal = local.leyenda_ticket;
    if (local.tipo_impresora) {
      cfg.tipoImpresora = local.tipo_impresora;
      cfg.ancho = local.tipo_impresora === '58mm' ? 58 : 80;
    }
    cfg.esPropio = Boolean(local.es_propio);
  } catch {
    // local config no disponible, usar defaults
  }

  if (isNativePlatform()) {
    _configCache = { ...cfg };
    _configCacheTime = now;
    return _configCache;
  }
  return cfg;
}

/**
 * Invalida la caché de configuración
 */
export function invalidarConfigCache(): void {
  _configCache = null;
  _configCacheTime = 0;
}

// ─── Payload builder ─────────────────────────────────────

export interface CrearPayloadOpts {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha?: string;
  metodoPago: string;
  referencia?: string | null;
  total: number;
  jugadas: TicketJugada[];
  vendedorNombre?: string;
  localNombre?: string;
  local?: TicketLocalInfo;
  esPropio?: boolean;
  esReimpresion?: boolean;
  tipoTicket?: string;
  pagaVecesAplicado?: string | null;
  clienteNombre?: string;
  estado?: string;
  fechaAnulacion?: string;
}

export async function crearTicketPayload(opts: CrearPayloadOpts): Promise<TicketPayload> {
  const config = await resolverTicketConfig();

  const localInfo: TicketLocalInfo = {
    nombre: (opts.local?.nombre || opts.localNombre || '').trim(),
    descripcion: opts.local?.descripcion || null,
    telefono: opts.local?.telefono || null,
    logo: opts.local?.logo || null,
    esPropio: opts.local?.esPropio ?? opts.esPropio ?? false,
    grupoId: opts.local?.grupoId ?? null,
  };

  return {
    ticketCode: opts.ticketCode,
    ticketId: opts.ticketId,
    sorteoNombre: opts.sorteoNombre,
    sorteoCierre: opts.sorteoCierre,
    sorteoFecha: opts.sorteoFecha || '',
    fecha: new Date().toLocaleString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
    vendedorNombre: opts.vendedorNombre || '',
    localNombre: localInfo.nombre,
    local: localInfo,
    esPropio: localInfo.esPropio ?? false,
    metodoPago: opts.metodoPago,
    referencia: opts.referencia || null,
    total: opts.total,
    jugadas: opts.jugadas,
    config,
    esReimpresion: opts.esReimpresion || false,
    tipoTicket: opts.tipoTicket || 'NUEVO',
    pagaVecesAplicado: opts.pagaVecesAplicado ?? null,
    clienteNombre: opts.clienteNombre || 'Cliente',
    estado: opts.estado,
    fechaAnulacion: opts.fechaAnulacion,
  };
}

// ─── HTML Render ─────────────────────────────────────────

/** Mapea TicketFontWeight → CSS font-weight */
function cssWeight(w: TicketFontWeight): string {
  return w === 'extra-bold' ? '900' : w === 'bold' ? '700' : '400';
}

function cssStrongWeight(base: string): string {
  const n = parseInt(base, 10);
  return String(Math.min(900, n + 200));
}

function cssSectionWeight(base: string): string {
  const n = parseInt(base, 10);
  return String(Math.min(900, n + 300));
}

// ─── Annulment visual markers ───────────────────────────

function anulacionCSS(): string {
  return `
/* ── Annulment markers ── */
.anulado-banner {
  background: #c00; color: #fff; text-align: center; font-weight: 900;
  font-size: 14px; padding: 6px 4px; margin: 0 0 4px; letter-spacing: 1px;
  border: 3px solid #000;
}
.anulado-watermark {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  pointer-events: none; z-index: 0;
  overflow: hidden;
}
.anulado-watermark-text {
  font-size: 48px; font-weight: 900; color: rgba(200,0,0,0.15);
  transform: rotate(-30deg); white-space: nowrap;
  letter-spacing: 8px;
}
.anulado-sello {
  border: 3px solid #c00; text-align: center; font-weight: 900;
  font-size: 16px; color: #c00; padding: 4px; margin: 4px 0;
  letter-spacing: 2px;
}
.anulado-pie {
  text-align: center; font-weight: 900; font-size: 10px;
  border-top: 2px dashed #000; padding-top: 4px; margin-top: 4px;
  line-height: 1.5;
}
.anulado-jugada { text-decoration: line-through; color: #666; }
@media print {
  .anulado-banner, .anulado-sello, .anulado-pie,
  .anulado-watermark { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;
}

function anulacionBannerHTML(): string {
  return `<div class="anulado-banner" role="alert" aria-label="Ticket anulado - no válido">*** TICKET ANULADO — NO VALIDO ***</div>`;
}

function anulacionWatermarkHTML(): string {
  return `<div class="anulado-watermark" aria-hidden="true"><div class="anulado-watermark-text">ANULADO&nbsp;&nbsp;&nbsp;ANULADO&nbsp;&nbsp;&nbsp;ANULADO</div></div>`;
}

function anulacionSelloHTML(): string {
  return `<div class="anulado-sello">** ANULADO **</div>`;
}

function anulacionPieHTML(payload: TicketPayload): string {
  const fechaAnul = payload.fechaAnulacion || '—';
  const ticketNum = formatTicketForPrint(payload.ticketCode || payload.ticketId || '—');
  return `<div class="anulado-pie">
*** ESTE TICKET FUE ANULADO — NO VALIDO PARA COBRO ***<br/>
Fecha de anulación: ${escHtml(fechaAnul)}<br/>
Ticket N°: ${escHtml(ticketNum)} (se muestra para verificación)
</div>`;
}

/** Mapea TicketFontSize → tamaños px (optimizados para impresoras térmicas) */
function cssSizes(sz: TicketFontSize, anchoMM: TicketAncho) {
  const map: Record<TicketAncho, Record<TicketFontSize, { body: number; monto: number; nums: number; total: number; nombre: number; seccion: number }>> = {
    58: {
      'normal':       { body: 11, monto: 12, nums: 11, total: 14, nombre: 12, seccion: 11 },
      'grande':       { body: 12, monto: 13, nums: 12, total: 15, nombre: 13, seccion: 12 },
      'extra-grande': { body: 13, monto: 14, nums: 13, total: 16, nombre: 14, seccion: 13 },
      'gigante':      { body: 14, monto: 16, nums: 15, total: 18, nombre: 15, seccion: 14 },
      'extra-gigante':{ body: 15, monto: 18, nums: 17, total: 20, nombre: 17, seccion: 16 },
    },
    80: {
      'normal':       { body: 16, monto: 19, nums: 18, total: 22, nombre: 18, seccion: 17 },
      'grande':       { body: 17, monto: 20, nums: 19, total: 23, nombre: 19, seccion: 18 },
      'extra-grande': { body: 19, monto: 22, nums: 21, total: 25, nombre: 21, seccion: 20 },
      'gigante':      { body: 21, monto: 27, nums: 25, total: 29, nombre: 24, seccion: 22 },
      'extra-gigante':{ body: 23, monto: 31, nums: 29, total: 33, nombre: 27, seccion: 24 },
    },
  };
  return map[anchoMM][sz];
}

// ═══════════════════════════════════════════════════════════════════════════
// PLANTILLA DEDICADA 58mm — Layout optimizado para papel térmico angosto
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Genera HTML específico para ticket 58mm.
 * Diseño compacto, texto legible, sin overflow.
 * NO usa grids complejos — usa texto monoespaciado simple.
 */
function renderTicket58mm(payload: TicketPayload): string {
  const { config, jugadas, esReimpresion } = payload;
  const esAnulado = payload.estado === 'ANULADO';
  const bloques = buildJugadasBlock(jugadas);
  const tieneMonazos = bloques.some((bloque) => bloque.tipo === 'monazos');

  const nombreLocal = (payload.local?.nombre || payload.localNombre || '').trim().toUpperCase();
  const descripcion = (payload.local?.descripcion || '').trim();
  const sorteo = (payload.sorteoNombre || '').trim().toUpperCase();
  const pagaDisplay = payload.pagaVecesAplicado || null;
  const esTicketMonazos58 = tieneMonazos || /EN ORDEN|DESORDEN/i.test(pagaDisplay || '');
  const pagaHtml = pagaDisplay
    ? pagaDisplay
        .split('|')
        .map((parte) => parte.trim())
        .filter(Boolean)
        .map((parte, index) => index === 0 ? escHtml(parte) : `&nbsp;&nbsp;${escHtml(parte)}`)
        .join('<br>')
    : null;
  const cliente = (payload.clienteNombre || 'Cliente').trim();
  const telefono = (payload.local?.telefono || '').trim();
  const leyenda = config.leyendaLocal || 'GRACIAS POR SU PREFERENCIA';
  const ticketId = formatTicketForPrint(payload.ticketId || payload.ticketCode);
  const esPropio = payload.local?.esPropio ?? payload.esPropio ?? config.esPropio ?? false;

  const logoBlock = esPropio
    ? `<div class="logo"><img class="ticket-logo-propio" src="/img/ticket-header-barahona-banner.png?v=2" alt="BARAHONA"/></div>`
    : '';

  const reimpBlock = esReimpresion
    ? `<div class="reimp">*** REIMPRESION ***</div>`
    : '';

  const jugadasHTML = bloques.map((bloque) => {
    if (bloque.tipo === 'titulo') {
      return `<div class="tipo58">${escHtml(bloque.texto)}</div>`;
    }

    if (bloque.tipo === 'monazos') {
      return `
<div class="tipo58">${escHtml(bloque.titulo)}</div>
${bloque.lineas.map((linea, index) => `<div class="fila58 fila58-monazo"><span class="monto58">${index === 0 ? escHtml(bloque.monto) : ''}</span><span class="nums58">${escHtml(linea)}</span></div>`).join('')}`;
    }

    if (bloque.tipo === 'reventado-tabla') {
      // 58mm: reagrupar de 4→3 números por fila para evitar salto de línea
      const filas58: typeof bloque.filas = [];
      for (const fila of bloque.filas) {
        const nums = fila.numeros.split('-');
        for (let i = 0; i < nums.length; i += 3) {
          filas58.push({ numeros: nums.slice(i, i + 3).join('-'), normal: fila.normal, reventado: fila.reventado });
        }
      }
      return `
<div class="rev-head58"><span class="rev-num58">NUMS</span><span class="rev-monto58">DIR</span><span class="rev-monto58">REV</span></div>
${filas58.map((fila) => `<div class="rev-row58"><span class="rev-num58">${escHtml(fila.numeros)}</span><span class="rev-monto58">${escHtml(fila.normal || '-')}</span><span class="rev-monto58">${escHtml(fila.reventado || '-')}</span></div>`).join('')}`;
    }

    // 58mm: reagrupar líneas de 4→3 números para evitar desborde
    const lineas58: string[] = [];
    const allNums = bloque.lineas.join(' | ').split(' | ');
    for (let i = 0; i < allNums.length; i += 3) {
      lineas58.push(allNums.slice(i, i + 3).join(' | '));
    }
    return lineas58.map((linea, index) => `<div class="fila58"><span class="monto58">${index === 0 ? escHtml(bloque.monto) : ''}</span><span class="nums58">${escHtml(linea)}</span></div>`).join('');
  }).join('');

  const esReventadoTabla = bloques.some(b => b.tipo === 'reventado-tabla');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Ticket ${payload.ticketCode}</title>
<style>
@page { size: 48mm auto; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  margin: 0;
  padding: 0;
  background: #fff;
  color: #000;
  font-family: 'Courier New', Courier, monospace;
  overflow: visible;
}
.ticket.ticket-58 {
  width: 46.8mm;
  max-width: 46.8mm;
  margin: 0 auto;
  padding: 1.6mm 1.35mm 3.6mm;
  overflow: visible;
  font-family: 'Courier New', Courier, monospace;
  font-size: 11.4px;
  font-weight: 700;
  font-stretch: semi-expanded;
  line-height: 1.26;
  letter-spacing: 0.1px;
  color: #000;
  background: #fff;
}
.logo { text-align: center; margin: 0 0 1px; line-height: 0; }
.logo img { width: 100%; max-height: 64px; object-fit: contain; }
.ticket-logo-propio { display: block; width: 100%; max-width: 52mm; max-height: 12mm; object-fit: contain; margin: 0 auto 1mm; }
@media print { .ticket-logo-propio { page-break-inside: avoid; break-inside: avoid; } }
.local { text-align: center; font-size: 14.5px; font-weight: 900; line-height: 1.12; }
.desc { text-align: center; font-size: 11.1px; margin-top: 1px; }
.sorteo { text-align: center; font-size: 13.2px; font-weight: 900; line-height: 1.16; margin: 2px 0; padding: 2px 0; }
.sep {
  border: 0;
  border-top: 1px dashed #000;
  margin: 3px 0;
}
.meta-box58 { margin: 2px 0 1px; }
.meta-row58 {
  display: grid;
  grid-template-columns: 52px 1fr;
  column-gap: 4px;
  align-items: start;
  margin: 1px 0;
  font-size: 11.35px;
  line-height: 1.24;
}
.meta-label58 { font-weight: 900; }
.meta-value58 { text-align: left; }
.reimp { text-align: center; font-weight: 900; border: 1px dashed #000; margin: 0 0 3px; padding: 2px 0; font-size: 11px; }
.spacer58 { height: 2px; }
.detalle58 { text-align: center; font-size: 12.5px; font-weight: 900; margin: 1px 0; padding: 1px 0; letter-spacing: 0.25px; }
.header58 {
  display: grid;
  grid-template-columns: 62px 1fr;
  column-gap: 4px;
  font-weight: 900;
  margin-bottom: 1px;
  font-size: 11px;
}
.tipo58 { text-align: left; font-size: 12px; font-weight: 900; margin: 2px 0 1px; }
.fila58 {
  display: grid;
  grid-template-columns: 62px 1fr;
  column-gap: 4px;
  align-items: start;
  margin: 1px 0;
}
.fila58-monazo { margin-bottom: 2px; }
.monto58 { display: block; font-weight: 900; white-space: nowrap; }
.nums58 { display: block; text-align: left; white-space: pre-wrap; word-break: break-word; }
.rev-head58,
.rev-row58 {
  display: grid;
  grid-template-columns: 1fr 44px 44px;
  column-gap: 4px;
  align-items: start;
}
.rev-head58 { font-size: 10px; font-weight: 900; margin: 1px 0 2px; }
.rev-row58 { font-size: 10.7px; margin: 1px 0; }
.rev-num58 { text-align: left; }
.rev-monto58 { text-align: right; white-space: nowrap; }
.total { font-size: 15.5px; font-weight: 900; margin: 3px 0 2px; line-height: 1.15; }
.nota { text-align: center; font-size: 10.2px; line-height: 1.2; margin-top: 4px; margin-bottom: 1px; }
.ticket-id { font-size: 10.3px; font-weight: 700; margin-top: 2px; padding-bottom: 1px; }
@media print {
  html { height: auto !important; }
  body { margin: 0; padding: 0; height: auto !important; min-height: 0 !important; overflow: visible !important; }
  .ticket.ticket-58 { padding-bottom: 3.6mm; overflow: visible !important; }
}
${esAnulado ? anulacionCSS() : ''}
</style>
</head>
<body>
${esAnulado ? anulacionBannerHTML() : ''}
${esAnulado ? anulacionWatermarkHTML() : ''}
<div class="ticket ticket-58">
${reimpBlock}
${logoBlock}
<div class="local">${escHtml(nombreLocal)}</div>
${descripcion ? `<div class="desc">${escHtml(descripcion)}</div>` : ''}
${sorteo ? `<div class="sorteo">${escHtml(sorteo)}</div>` : ''}

<div class="meta-box58">
  <div class="meta-row58"><span class="meta-label58">VENTA:</span><span class="meta-value58">${escHtml(payload.fecha)}</span></div>
  ${pagaHtml && !esTicketMonazos58 ? `<div class="meta-row58"><span class="meta-label58">Paga:</span><span class="meta-value58">${pagaHtml}</span></div>` : ''}
  <div class="meta-row58"><span class="meta-label58">CLI:</span><span class="meta-value58">${escHtml(cliente)}</span></div>
  ${telefono ? `<div class="meta-row58"><span class="meta-label58">TEL:</span><span class="meta-value58">${escHtml(telefono)}</span></div>` : ''}
</div>

<div class="spacer58"></div>

<hr class="sep"/>
<div class="detalle58">DETALLE</div>
<hr class="sep"/>

${!esReventadoTabla ? `<div class="header58"><span>MONTO</span><span>NUMEROS</span></div><hr class="sep"/>` : ''}

${esAnulado ? `<div class="anulado-jugada">` : ''}
${jugadasHTML}
${esAnulado ? `</div>` : ''}

<hr class="sep"/>
<div class="total">TOTAL: ₡${payload.total.toLocaleString('es-CR')}</div>
${esAnulado ? anulacionSelloHTML() : ''}

<hr class="sep"/>

<div class="nota">${escHtml(leyenda)}</div>
<div class="ticket-id">TIQUETE: ${escHtml(ticketId)}</div>
${esAnulado ? anulacionPieHTML(payload) : ''}
</div>
</body>
</html>`;
}

/**
 * Genera HTML completo del ticket térmico en formato Barahona.
 * Solo afecta presentación; no modifica lógica de ventas ni backend.
 */
export function renderTicketHTML(payload: TicketPayload): string {
  if (isNativePlatform()) return renderTicketHTMLActual(payload);
  const configWeb = normalizarConfiguracionWeb(payload.config.configuracionWeb);
  const base = configWeb && configWeb.perfil_tamano !== 'actual'
    ? { ...payload, config: { ...payload.config, fontSize: 'normal' as TicketFontSize } } : payload;
  return aplicarConfiguracionWeb(compactarMetadatosWeb(renderTicketHTMLActual(base)), configWeb);
}

function renderTicketHTMLActual(payload: TicketPayload): string {
  const { config } = payload;
  const anchoMM = config.ancho;

  // ── PLANTILLA DEDICADA PARA 58mm ──
  if (anchoMM === 58) {
    return renderTicket58mm(payload);
  }

  // ── PLANTILLA 80mm (sin cambios) ──
  const { jugadas, esReimpresion } = payload;
  const esAnulado80 = payload.estado === 'ANULADO';
  // ── Valores fijos para 80mm ──
  const anchoPx = 300;
  const charWidth = 34;
  const colMeta = 76;
  const colMonto = 72;
  const colRev = 68;
  const sz = cssSizes(config.fontSize, 80);
  const weight = cssWeight(config.fontWeight);
  const weightStrong = cssStrongWeight(weight);
  const bloques = buildJugadasBlock(jugadas);
  const esReventadoTabla = bloques.some(b => b.tipo === 'reventado-tabla');
  const premios = parsearPremiosTicket(payload.pagaVecesAplicado);

  // ── Encabezado por local activo ──
  const nombreMostrar = (payload.local?.nombre || payload.localNombre || '').trim().toUpperCase();
  const descripcionMostrar = (payload.local?.descripcion || '').trim();
  const telefonoMostrar = (payload.local?.telefono || '').trim();
  const clienteMostrar = (payload.clienteNombre || 'Cliente').trim();
  const vendedorMostrar = (payload.vendedorNombre || '').trim();
  // ── Sorteo y método de pago ──
  const sorteoMostrar = (payload.sorteoNombre || '').trim();
  const pagoMostrar = (payload.metodoPago || 'EFECTIVO').toUpperCase();
  const pagaDisplay = payload.pagaVecesAplicado || null;

  // ── Logo: solo si el local es propio (es_propio == true) ──
  const esPropio = payload.local?.esPropio ?? payload.esPropio ?? config.esPropio ?? false;
  const logoBlock = esPropio
    ? `<div style="text-align:center;margin:0 0 2mm;padding:0;line-height:0"><img class="ticket-logo-propio" src="/img/ticket-header-barahona-banner.png?v=2" alt="BARAHONA" /></div>`
    : '';

  // ── Reimpresión ──
  const reimpBlock = esReimpresion
    ? `<div class="reimp">******** REIMPRESIÓN ********</div>`
    : '';

  // ── Disclaimer reimpresión ──
  const reimpDisclaimer = '';

  // ── FASE 1: RRR Ajuste PRO #2 — leyenda configurable local ──
  const leyendaMostrar = config.leyendaLocal || 'GRACIAS POR SU PREFERENCIA';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Ticket ${payload.ticketCode}</title>
<style>
@page { size: 80mm auto; margin: 0; }
* { margin:0; padding:0; box-sizing:border-box; }
body {
  width: 80mm;
  max-width: 80mm;
  overflow: visible;
  word-break: break-word;
  font-family: 'Courier New', Courier, monospace;
  font-size: ${sz.body}px;
  font-weight: ${weight};
  line-height: 1.22;
  color: #000;
  background: #fff;
  padding: 1px 5px 8px;
  margin: 0 auto;
}
.ticket {
  width: 100%;
  max-width: 100%;
  overflow: visible;
  word-break: break-word;
  font-family: 'Courier New', Courier, monospace;
  font-size: ${sz.body}px;
  line-height: 1.22;
}
.ticket.ticket-80 {
  width: 72mm;
  max-width: 72mm;
  margin: 0 auto;
}
.center { text-align: center; }
.reimp {
  text-align: center;
  padding: 2px 0;
  border: 1px dashed #333;
  margin-bottom: 2px;
}
.title {
  font-size: ${Math.max(13, sz.nombre)}px;
  font-weight: ${weightStrong};
}
.sub-title {
  font-size: ${Math.max(12, sz.body)}px;
  margin-bottom: 1px;
}
.line {
  margin-top: 1px;
  margin-bottom: 2px;
  padding-bottom: 1px;
}
.meta-box {
  margin: 2px 0 1px;
}
.meta-row {
  display: grid;
  grid-template-columns: ${colMeta}px 1fr;
  column-gap: 4px;
  align-items: start;
  margin: 1px 0;
}
.meta-label {
  font-weight: ${weightStrong};
}
.meta-value {
  text-align: left;
}
.sorteo-destacado {
  text-align: center;
  font-size: ${Math.max(14, sz.seccion + 2)}px;
  font-weight: ${cssSectionWeight(weight)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 2px 0;
  padding: 2px 0;
  border: 0;
}
.spacer {
  height: 2px;
}
.sep {
  border: 0;
  border-top: 1px dashed #000;
  margin: 3px 0;
}
.detalle {
  font-weight: ${weightStrong};
  text-align: center;
  margin: 1px 0;
  padding: 1px 0;
}
.row {
  display: grid;
  grid-template-columns: ${colMonto}px 1fr;
  align-items: start;
  text-align: left;
  column-gap: 6px;
  margin: 1px 0;
}
.header {
  font-weight: ${weightStrong};
  margin-bottom: 1px;
}
.monto {
  width: ${colMonto}px;
  flex: 0 0 ${colMonto}px;
  font-weight: ${weightStrong};
}
.nums {
  flex: 1;
  word-break: break-word;
}
.tipo-seccion {
  text-align: left;
  font-weight: ${weightStrong};
  margin: 0;
}
.premios-box {
  margin: 0;
}
.premio-row {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 4px;
  margin: 0;
}
.premio-label {
  text-align: left;
}
.premio-valor {
  text-align: right;
  font-weight: ${weightStrong};
}
.total {
  font-size: ${sz.total}px;
  font-weight: ${weightStrong};
}
.center-note {
  text-align: center;
  margin: 0;
}
.footer {
  margin-top: 0;
}
.reimp-disclaimer {
  text-align: left;
  margin: 0;
}
.rev-tabla {
  margin: 0;
}
.rev-header, .rev-row {
  display: grid;
  grid-template-columns: 1fr 4px ${colRev}px 4px ${colRev}px;
  column-gap: 0;
  align-items: center;
}
.rev-header {
  font-weight: ${weightStrong};
  font-size: ${Math.max(10, sz.nums - 2)}px;
}
.rev-nums {
  text-align: left;
}
.rev-sep {
  text-align: center;
  color: #666;
}
.rev-col {
  text-align: center;
  font-size: ${Math.max(10, sz.nums - 1)}px;
}
.ticket-logo-propio { display: block; width: 100%; max-width: 72mm; max-height: 16mm; object-fit: contain; margin: 0 auto 2mm; }
@media print {
  html { height: auto !important; overflow: visible !important; }
  body { margin: 0; padding: 0 3px 6px; height: auto !important; min-height: 0 !important; max-height: max-content !important; overflow: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .ticket { font-weight: inherit; height: auto !important; overflow: visible !important; }
  .ticket-logo-propio { page-break-inside: avoid; break-inside: avoid; }
}
${esAnulado80 ? anulacionCSS() : ''}
</style>
</head>
<body>
${esAnulado80 ? anulacionBannerHTML() : ''}
${esAnulado80 ? anulacionWatermarkHTML() : ''}
<div class="ticket ticket-80">
${reimpBlock}
${logoBlock}
<div class="center title">${escHtml(nombreMostrar || config.nombre || '')}</div>
${descripcionMostrar ? `<div class="center sub-title">${escHtml(descripcionMostrar)}</div>` : ''}
${sorteoMostrar ? `<div class="sorteo-destacado">${escHtml(sorteoMostrar)}</div>` : ''}

<div class="meta-box">
  <div class="meta-row"><span class="meta-label">VENTA:</span><span class="meta-value">${escHtml(payload.fecha)}</span></div>
  ${pagaDisplay ? `<div class="meta-row"><span class="meta-label">Paga:</span><span class="meta-value">${escHtml(pagaDisplay)}</span></div>` : ''}
  <div class="meta-row"><span class="meta-label">CLIENTE:</span><span class="meta-value">${escHtml(clienteMostrar)}</span></div>
  ${vendedorMostrar ? `<div class="meta-row"><span class="meta-label">VENDEDOR:</span><span class="meta-value">${escHtml(vendedorMostrar)}</span></div>` : ''}
  ${telefonoMostrar ? `<div class="meta-row"><span class="meta-label">TEL:</span><span class="meta-value">${escHtml(telefonoMostrar)}</span></div>` : ''}
</div>

<div class="spacer"></div>

<hr class="sep"/>
<div class="detalle">DETALLE</div>
<hr class="sep"/>

${!esReventadoTabla ? `<div class="row header">
  <span class="monto">MONTO</span>
  <span class="nums">NUMEROS</span>
</div>
<hr class="sep"/>` : ''}

${esAnulado80 ? `<div class="anulado-jugada">` : ''}
${bloques.map((bloque) => {
  if (bloque.tipo === 'titulo') {
    return `<div class="tipo-seccion">${escHtml(bloque.texto)}</div>`;
  }
  if (bloque.tipo === 'monazos') {
    const [primera, ...extra] = bloque.lineas;
    return `<div class="tipo-seccion">${escHtml(bloque.titulo)}</div><div class="row"><span class="monto">${escHtml(bloque.monto)}</span><span class="nums">${escHtml(primera || '')}</span></div>${extra.map((linea) => `<div class="row"><span class="monto"></span><span class="nums">${escHtml(linea)}</span></div>`).join('')}<div class="spacer"></div>`;
  }
  if (bloque.tipo === 'reventado-tabla') {
    return `<div class="rev-tabla">
<div class="rev-header"><span class="rev-nums">NUMEROS</span><span class="rev-sep">|</span><span class="rev-col">DIR</span><span class="rev-sep">|</span><span class="rev-col">REV</span></div>
<hr class="sep"/>
${bloque.filas.map((fila) => `<div class="rev-row"><span class="rev-nums">${escHtml(fila.numeros)}</span><span class="rev-sep">|</span><span class="rev-col">${escHtml(fila.normal)}</span><span class="rev-sep">|</span><span class="rev-col">${escHtml(fila.reventado)}</span></div>`).join('')}
</div>`;
  }

  const [primera, ...extra] = bloque.lineas;
  return `<div class="row"><span class="monto">${escHtml(bloque.monto)}</span><span class="nums">${escHtml(primera || '')}</span></div>${extra.map((linea) => `<div class="row"><span class="monto"></span><span class="nums">${escHtml(linea)}</span></div>`).join('')}<div class="spacer"></div>`;
}).join('')}
${esAnulado80 ? `</div>` : ''}

<hr class="sep"/>
<div class="total">TOTAL: ₡${payload.total.toLocaleString('es-CR')}</div>
${esAnulado80 ? anulacionSelloHTML() : ''}

<hr class="sep"/>

<div class="center-note">${escHtml(leyendaMostrar)}</div>

<div class="line">TIQUETE: ${escHtml(formatTicketForPrint(payload.ticketId || payload.ticketCode))}</div>
${reimpDisclaimer}
${esAnulado80 ? anulacionPieHTML(payload) : ''}
</div>
</body>
</html>`;
}

/**
 * Agrupa jugadas por monto → bloques limpios.
 *
 * Formato inline (monto y números en misma línea):
 *   ₡1,000   22 23 24 25 26 27 28
 *
 * Si los números no caben, continúan en la siguiente línea
 * alineados bajo los números (no bajo el monto).
 *
 * Formatea según tipo de sección:
 *  - REVENTADO: 22(R1) 23(R2)
 *  - MEGA: extrae número mega de num2
 *  - PARLEY: num1+num2
 *  - MONAZOS: número con tipo
 *  - NORMAL/FECHAS: num1 directo
 */
function buildJugadasBlock(jugadas: TicketJugada[]): BloqueJugadas[] {
  if (!jugadas.length) return [];

  const monazoLabels: Record<string, string> = {
    ORDEN: 'En Orden',
    DESORDEN: 'Desorden',
    P2: 'Primeros 2',
    U2: 'Ultimos 2',
  };

  // Detectar MONAZOS: todos los num2 son un tipo de monazo
  const esMonazos = jugadas.every((jugada) => !!jugada.num2 && Object.prototype.hasOwnProperty.call(monazoLabels, jugada.num2));

  if (esMonazos) {
    const TIPOS_OCULTOS_EN_TICKET = new Set(['P2', 'U2']);
    const ordenTipos = ['ORDEN', 'DESORDEN', 'P2', 'U2'];
    // Agrupar por tipo + monto para luego hacer bloques de 4
    const grupos = new Map<string, Map<number, string[]>>();

    for (const jugada of jugadas) {
      const tipo = jugada.num2 as string;
      if (!grupos.has(tipo)) grupos.set(tipo, new Map());
      const porMonto = grupos.get(tipo)!;
      const lista = porMonto.get(jugada.monto) || [];
      lista.push(jugada.num1);
      porMonto.set(jugada.monto, lista);
    }

    const bloques: BloqueJugadas[] = [];
    for (const tipo of ordenTipos) {
      if (TIPOS_OCULTOS_EN_TICKET.has(tipo)) continue;
      const porMonto = grupos.get(tipo);
      if (!porMonto) continue;
      for (const [monto, numeros] of porMonto) {
        const lineas: string[] = [];
        for (let i = 0; i < numeros.length; i += 4) {
          lineas.push(numeros.slice(i, i + 4).join(' | '));
        }
        bloques.push({
          tipo: 'monazos' as const,
          titulo: monazoLabels[tipo],
          monto: '₡' + monto.toLocaleString('es-CR'),
          lineas,
        });
      }
    }
    return bloques;
  }

  // Detectar REVENTADO: alguna jugada tiene num2 = 'REV1' o 'REV2'
  const esReventado = jugadas.some(j => j.num2 === 'REV1' || j.num2 === 'REV2');

  if (esReventado) {
    // Paso 1: Agrupar por número, recolectando monto directo y reventado
    const porNumero = new Map<string, { directo: number | null; reventado: number | null }>();

    for (const jugada of jugadas) {
      const num = jugada.num1;
      const esRev2 = jugada.num2 === 'REV2';

      if (!porNumero.has(num)) {
        porNumero.set(num, { directo: null, reventado: null });
      }
      const entry = porNumero.get(num)!;
      if (esRev2) {
        entry.reventado = (entry.reventado ?? 0) + jugada.monto;
      } else {
        entry.directo = (entry.directo ?? 0) + jugada.monto;
      }
    }

    // Paso 2: Agrupar números con la misma combinación de montos
    const porMontoCombo = new Map<string, string[]>();
    for (const [num, montos] of porNumero) {
      const key = `${montos.directo ?? ''}_${montos.reventado ?? ''}`;
      if (!porMontoCombo.has(key)) {
        porMontoCombo.set(key, []);
      }
      porMontoCombo.get(key)!.push(num);
    }

    // Paso 3: Construir filas (agrupar hasta 4 números por fila)
    const filas: Array<{ numeros: string; normal: string; reventado: string }> = [];

    for (const [key, numeros] of porMontoCombo) {
      const [directoStr, reventadoStr] = key.split('_');
      const directo = directoStr ? Number(directoStr) : null;
      const reventado = reventadoStr ? Number(reventadoStr) : null;

      // Agrupar de 4 en 4
      for (let i = 0; i < numeros.length; i += 4) {
        const grupo = numeros.slice(i, i + 4);
        filas.push({
          numeros: grupo.join('-'),
          normal: directo !== null ? '₡' + directo.toLocaleString('es-CR') : '',
          reventado: reventado !== null ? '₡' + reventado.toLocaleString('es-CR') : '',
        });
      }
    }

    return [{ tipo: 'reventado-tabla', filas }];
  }

  // General: NORMAL/TICA, FECHAS, PARLEY, MEGA (directo)
  return _agruparPorMonto(jugadas);
}

/** Agrupa jugadas por monto formando bloques de hasta 4 números por línea. */
function _agruparPorMonto(jugadas: TicketJugada[]): Array<{ tipo: 'general'; monto: string; lineas: string[] }> {
  const montoMap = new Map<number, string[]>();
  for (const jugada of jugadas) {
    const lista = montoMap.get(jugada.monto) || [];
    lista.push(numeroParaTicket(jugada));
    montoMap.set(jugada.monto, lista);
  }
  const bloques: Array<{ tipo: 'general'; monto: string; lineas: string[] }> = [];
  for (const [monto, numeros] of montoMap) {
    const lineas: string[] = [];
    for (let i = 0; i < numeros.length; i += 4) {
      lineas.push(numeros.slice(i, i + 4).join(' | '));
    }
    bloques.push({ tipo: 'general', monto: '₡' + monto.toLocaleString('es-CR'), lineas });
  }
  return bloques;
}

const MESES_CORTO: Record<string, string> = {
  '01': 'Ene', '02': 'Febr', '03': 'Marz', '04': 'Abr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Oct', '11': 'Nov', '12': 'Dic',
};

function numeroParaTicket(jugada: TicketJugada): string {
  if (jugada.num2 && /^MEGA:/i.test(jugada.num2)) {
    return jugada.num2.replace(/^MEGA:/i, '');
  }
  if (jugada.num2 && /^\d{2}$/.test(jugada.num2)) {
    return `${jugada.num1}-${jugada.num2}`;
  }
  // FECHAS: convertir DD-MM → DD-Mes
  if (jugada.tipoSorteo === 'FECHAS') {
    const match = jugada.num1.match(/^(\d{1,2})-(\d{2})$/);
    if (match) {
      const mes = MESES_CORTO[match[2]] || match[2];
      return `${match[1]}-${mes}`;
    }
  }
  return jugada.num1;
}

function parsearPremiosTicket(pagaVecesAplicado: string | null): PremioTicketLinea[] {
  if (!pagaVecesAplicado) return [];

  return pagaVecesAplicado
    .split('|')
    .map((parte) => parte.trim())
    .filter(Boolean)
    .map((parte) => {
      const match = parte.match(/^(.*?)(\d+(?:\.\d+)?)$/);
      if (!match) {
        return null;
      }

      return {
        etiqueta: match[1].trim(),
        valor: match[2].trim(),
      };
    })
    .filter((parte): parte is PremioTicketLinea => !!parte && parte.etiqueta.length > 0 && parte.valor.length > 0);
}

/** Escapa HTML básico */
function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── Inline render (para preview dentro de React) ────────

/**
 * Genera el HTML del ticket body (sin <html>/<head>) para usar dentro de un iframe srcdoc.
 * Usa barcode inline vía canvas.
 */
export function renderTicketPreviewHTML(payload: TicketPayload): string {
  // Usamos el HTML completo para el iframe
  return renderTicketHTML(payload);
}

// ─── Impresión ───────────────────────────────────────────

/**
 * Abre ventana temporal, imprime y la cierra automáticamente.
 * Usa onafterprint + fallback setTimeout para compatibilidad.
 */
function abrirVentanaImpresion(html: string, ancho: number): boolean {
  const w = ancho === 58 ? 420 : 560;
  const printWindow = window.open('', '_blank', `width=${w},height=700`);
  if (!printWindow) {
    console.error('[ticketService] No se pudo abrir ventana de impresión');
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  // Cerrar ventana después de imprimir (o cancelar)
  let cerrada = false;
  const cerrar = () => {
    if (cerrada) return;
    cerrada = true;
    try { printWindow.close(); } catch (_) { /* ya cerrada */ }
    // Firefox: close() puede fallar silenciosamente; limpiar si sigue abierta
    setTimeout(() => {
      try {
        if (!printWindow.closed) {
          printWindow.location.replace('about:blank');
          // Reintentar close sobre about:blank (Firefox lo permite a veces)
          setTimeout(() => {
            try { if (!printWindow.closed) printWindow.close(); } catch (_) { /* ignorar */ }
          }, 150);
        }
      } catch (_) { /* ventana inaccesible */ }
    }, 300);
  };

  printWindow.onload = () => {
    // onafterprint: se dispara al terminar/cancelar el diálogo de impresión
    if ('onafterprint' in printWindow) {
      printWindow.onafterprint = cerrar;
    }

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Fallback: si onafterprint no disparó, cerrar tras breve espera
      setTimeout(cerrar, 2000);
    }, 400);
  };

  return true;
}

/**
 * Imprime un ticket individual.
 * Abre ventana temporal, imprime y cierra automáticamente.
 */
export function imprimirTicket(payload: TicketPayload): boolean {
  const html = renderTicketHTML(payload);
  return abrirVentanaImpresion(html, payload.config.ancho);
}

function extraerStyleTicket(html: string): string {
  const match = html.match(/<style>([\s\S]*?)<\/style>/i);
  return match?.[1] || '';
}

function extraerBodyTicket(html: string): string {
  const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  return match?.[1] || html;
}

export function imprimirTicketsLote(payloads: TicketPayload[]): boolean {
  if (!payloads.length) {
    return false;
  }
  if (payloads.length === 1) {
    return imprimirTicket(payloads[0]);
  }

  const htmlBase = renderTicketHTML(payloads[0]);
  const styles = `${extraerStyleTicket(htmlBase)}\n.page-break { page-break-after: always; }\n.page-break:last-child { page-break-after: auto; }`;
  const bloques = payloads
    .map((payload, index) => {
      const body = extraerBodyTicket(renderTicketHTML(payload));
      return `${body}${index < payloads.length - 1 ? '<div class="page-break"></div>' : ''}`;
    })
    .join('');

  const printWindow = window.open('', '_blank', `width=${payloads[0].config.ancho === 58 ? 420 : 560},height=700`);
  if (!printWindow) {
    console.error('[ticketService] No se pudo abrir ventana de impresión para lote');
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Impresión de premios</title><style>${styles}</style></head><body>${bloques}</body></html>`);
  printWindow.document.close();

  let cerrada = false;
  const cerrar = () => {
    if (cerrada) return;
    cerrada = true;
    try { printWindow.close(); } catch (_) { /* ya cerrada */ }
    // Firefox: close() puede fallar silenciosamente; limpiar si sigue abierta
    setTimeout(() => {
      try {
        if (!printWindow.closed) {
          printWindow.location.replace('about:blank');
          setTimeout(() => {
            try { if (!printWindow.closed) printWindow.close(); } catch (_) { /* ignorar */ }
          }, 150);
        }
      } catch (_) { /* ventana inaccesible */ }
    }, 300);
  };

  printWindow.onload = () => {
    if ('onafterprint' in printWindow) {
      printWindow.onafterprint = cerrar;
    }
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(cerrar, 2000);
    }, 400);
  };
  return true;
}

function padRight(text: string, width: number): string {
  const value = text.length > width ? text.slice(0, width) : text;
  return value.padEnd(width, ' ');
}

function padLeft(text: string, width: number): string {
  const value = text.length > width ? text.slice(0, width) : text;
  return value.padStart(width, ' ');
}

function formatResumenMonto(value: number): string {
  return Math.round(Number(value || 0)).toString();
}

function renderResumenRows(lineas: ResumenPremiosLinea[]): string {
  return lineas.map((linea) => {
    const ticket = padRight(linea.ticket, 14);
    const monto = padLeft(formatResumenMonto(linea.monto), 8);
    const premio = padLeft(formatResumenMonto(linea.premio), 10);
    return `${ticket}${monto}${premio}`;
  }).join('\n');
}

export function renderResumenPremiosHTML(payload: ResumenPremiosPayload): string {
  const fechaImpresion = new Date().toLocaleString('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const localNombre = payload.local.nombre || '';
  const localDescripcion = payload.local.descripcion || '';
  const sorteoDetalle = payload.sorteoCierre ? `${payload.sorteo} ${payload.sorteoCierre}` : payload.sorteo;
  const totalPagado = formatResumenMonto(payload.totalPagado);
  const totalRecaudado = formatResumenMonto(payload.totalRecaudado);

  // Generar filas HTML directamente desde payload.tickets
  // Formato visual corto (sin año) — el ticket_number completo se conserva en BD.
  const filasHTML = payload.tickets.length > 0
    ? payload.tickets.map((t) => {
        const ticketCorto = formatTicketForPrint(t.ticket);
        const monto = formatResumenMonto(t.monto);
        const premio = formatResumenMonto(t.premio);
        return `<div class="tk-row"><span class="tk-id">#${escHtml(ticketCorto)}</span><span class="tk-val">₡${escHtml(monto)}</span><span class="tk-val tk-premio">₡${escHtml(premio)}</span></div>`;
      }).join('')
    : '<div class="center" style="margin:4px 0">SIN TICKETS</div>';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Resumen de premios</title>
<style>
@page { size: 48mm auto; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  width: 46.8mm;
  max-width: 46.8mm;
  overflow-x: hidden;
  overflow-wrap: break-word;
  word-break: break-word;
  margin: 0 auto;
  padding: 1.6mm 1.35mm 3.6mm;
  background: #fff;
  color: #000;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
}
.center { text-align: center; }
.logo { text-align: center; margin: 0; padding: 0; line-height: 0; }
.logo img { width: 100%; max-height: 60px; object-fit: contain; }
.title { font-size: 14px; font-weight: 900; margin-top: 2px; }
.subtitle { font-size: 12px; font-weight: 700; }
.sep { border: 0; border-top: 1px dashed #000; margin: 3px 0; }
.meta-box { margin: 2px 0; }
.meta-row {
  display: grid;
  grid-template-columns: 70px 1fr;
  column-gap: 4px;
  align-items: baseline;
  margin: 1px 0;
}
.meta-label { font-weight: 900; font-size: 11px; }
.meta-value { text-align: left; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tk-header {
  display: grid;
  grid-template-columns: 1fr 48px 48px;
  column-gap: 2px;
  font-weight: 900;
  font-size: 11px;
  margin: 2px 0;
}
.tk-header span:nth-child(2),
.tk-header span:nth-child(3) { text-align: right; }
.tk-row {
  display: grid;
  grid-template-columns: 1fr 48px 48px;
  column-gap: 2px;
  font-size: 10px;
  font-weight: 700;
  margin: 1px 0;
  border-bottom: 1px dotted #ccc;
  padding: 1px 0;
}
.tk-id { font-weight: 900; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tk-val { text-align: right; font-weight: 700; white-space: nowrap; }
.tk-premio { font-weight: 900; }
.totales { margin: 3px 0; }
.total-row {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 4px;
  font-weight: 900;
  font-size: 13px;
  margin: 2px 0;
}
.total-row span:last-child { text-align: right; white-space: nowrap; }
.footer { margin-top: 3px; font-size: 11px; font-weight: 700; }
.fecha-imp { font-size: 10px; font-weight: 700; margin-top: 2px; }
@media print {
  body { margin: 0; padding: 1px 2px 4px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
</style>
</head>
<body>
  <div class="center title">${escHtml(localNombre)}</div>
  ${localDescripcion ? `<div class="center subtitle">${escHtml(localDescripcion)}</div>` : ''}

  <hr class="sep"/>

  <div class="meta-box">
    <div class="meta-row"><span class="meta-label">FECHA:</span><span class="meta-value">${escHtml(payload.fecha)}</span></div>
    <div class="meta-row"><span class="meta-label">JUEGO:</span><span class="meta-value">${escHtml(sorteoDetalle)}</span></div>
    <div class="meta-row"><span class="meta-label">ESTADO:</span><span class="meta-value">${escHtml(payload.estado || 'CERRADO')}</span></div>
    <div class="meta-row"><span class="meta-label">GANADOR:</span><span class="meta-value">${escHtml(payload.ganador)}</span></div>
  </div>

  <hr class="sep"/>
  <div class="tk-header"><span>TICKET</span><span>MONTO</span><span>PREMIO</span></div>
  <hr class="sep"/>
  ${filasHTML}
  <hr class="sep"/>

  <div class="totales">
    <div class="total-row"><span>TOTAL:</span><span>${escHtml(totalPagado)}</span></div>
  </div>

  <hr class="sep"/>

  <div class="totales">
    <div class="total-row"><span>RECAUDADO:</span><span>${escHtml(totalRecaudado)}</span></div>
    <div class="total-row"><span>PAGADO:</span><span>${escHtml(totalPagado)}</span></div>
  </div>

  <hr class="sep"/>
  <div class="center footer">GRACIAS POR SU PREFERENCIA</div>
  <div class="center fecha-imp">${escHtml(fechaImpresion)}</div>
</body>
</html>`;
}

export function imprimirResumenPremios(payload: ResumenPremiosPayload): boolean {
  const html = renderResumenPremiosHTML(payload);
  return abrirVentanaImpresion(html, 58);
}

// ─── Post-venta: flujo único ─────────────────────────────

export interface PostVentaParams {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha?: string;
  metodoPago: string;
  referencia?: string | null;
  total: number;
  jugadas: TicketJugada[];
  vendedorNombre?: string;
  localNombre?: string;
  local?: TicketLocalInfo;
  esPropio?: boolean;
  pagaVecesAplicado?: string | null;
  clienteNombre?: string;
  estado?: string;
  fechaAnulacion?: string;
}

/**
 * procesarPostVenta: Punto de entrada único post-venta.
 * Genera el payload y lo retorna para que el UI decida mostrar preview.
 * 
 * NO modifica lógica de venta. Solo genera el payload del ticket.
 */
export async function procesarPostVenta(params: PostVentaParams): Promise<TicketPayload> {
  const payload = await crearTicketPayload({
    ...params,
    esReimpresion: false,
  });

  return payload;
}

/**
 * procesarReimpresion: Genera payload para reimpresión con marca.
 */
export async function procesarReimpresion(params: PostVentaParams): Promise<TicketPayload> {
  const payload = await crearTicketPayload({
    ...params,
    esReimpresion: true,
  });

  return payload;
}

// ─── Helpers ─────────────────────────────────────────────

function wrapText(texto: string, ancho: number): string {
  const words = texto.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if ((current + ' ' + word).trim().length > ancho) {
      if (current) lines.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }
  }
  if (current) lines.push(current);
  return lines.join('\n');
}
